import {
  JourneyApplicationEventTypesMap,
  JourneyApplicationStateTypesMap,
  JourneyNodeTypesMap,
  InvestigationStateTypesMap,
} from '@usealloy/typegen';

import type { JourneyFilters } from '~/analytics/journey-analytics/dashboard/filters/types';

export const business = 'business';

export const DEFAULT_MANUAL_REVIEW_OUTCOMES_NAMES = new Set([
  'approved', 'denied', 'withdrawn',
]);

export const ALERTS_MANUAL_REVIEW_OUTCOMES_NAMES = new Set([
  'dismissed', 'suspicious',
]);

export const JA_TERMINAL_OUTCOME_STATES = [
  JourneyApplicationStateTypesMap.COMPLETED,
  JourneyApplicationStateTypesMap.ERROR,
  JourneyApplicationStateTypesMap.EXPIRED,
] as const;

export const DEFAULT_MANUAL_REVIEW_NODE_ID = 'DEFAULT_MANUAL_REVIEW_NODE_ID';

/**
 * Post EOJ recon manual review nodes are not actually stored on the Journey version, they're
 * only visually displayed in the Journey graph. So we'll use this placeholder node id in order
 * to be able to enumerate possible outcomes for the post recon review form
 */
export const DEFAULT_POST_EOJ_RECON_MANUAL_REVIEW_NODE_ID = 'DEFAULT_POST_EOJ_RECON_MANUAL_REVIEW_NODE_ID';

/**
 * We allow post JA completion manual reviews. Before Journey custom outcomes, those review options
 * were only ever Approved/Denied or Dismissed/Suspicious based on the Journey type.
 *
 * Now the outcome options for those types of post completion reviews will be any outcomes
 * valid for the Journey type. This node id will be used to enumerate them when we
 * process outcomes during set up of the Application/Alert detail view
 */
export const POST_COMPLETED_JA_MANUAL_REVIEW_NODE_ID = 'POST_COMPLETED_JA_MANUAL_REVIEW_NODE_ID';

export const DEFAULT_QUERY = {
  filterBy: {
    journeyApplicationIsArchived: 'false',
    journeyApplicationIsSandbox: 'false',
  },
  searchBy: {},
};

export const EXCLUDE_EVENT_TYPES_FROM_JOURNEY_STEPS = new Set([
  JourneyApplicationEventTypesMap.STARTED_RECONCILIATION,
  JourneyApplicationEventTypesMap.COMPLETED_RECONCILIATION,
]);

export const FILTER_KEYS_TO_IGNORE_IN_SEARCH_BAR = [
  'journeyApplicationCreatedAt',
  'journeyApplicationFraudScores',
  'journeyApplicationUpdatedAt',
  'journeyType',
];

export const group = 'group';

export const groupTypes = [business, group];

export const NODE_TYPES_TO_MAP_TO_JOURNEY_STEP = new Set([
  JourneyNodeTypesMap.ACTION,
  JourneyNodeTypesMap.DOCV,
  JourneyNodeTypesMap.RECONCILIATION_WORKFLOW,
  JourneyNodeTypesMap.RETRY,
  JourneyNodeTypesMap.REVIEW,
  JourneyNodeTypesMap.SOURCE_WORKFLOW,
  JourneyNodeTypesMap.STEP_UP,
  JourneyNodeTypesMap.WORKFLOW,
]);

export const JOURNEYS_FILTERS: Array<JourneyFilters> = [
  'journeyApplicationCreatedAt',
  'journeyApplicationUpdatedAt',
  'childEntityType',
  'journeyToken',
  'journeyVersion',
  'journeyApplicationStatus',
  'journeyApplicationIsArchived',
  'journeyApplicationIsSandbox',
  'assignedAgent',
  'journeyChampionChallengerName',
  'journeyApplicationTags',
  'journeyApplicationReviewReasons',
  'journeyApplicationDataSources',
  'journeyApplicationReasons',
  'journeyApplicationFraudScores',
  'journeyApplicationReasonCodes',
  'branchName',
  'customerPartnerId',
  'evaluationResponseStatus',
  'journeyType',
  'journeyApplicationIsRerun',
  'latestEntityOutcomes',
];

// for Application Table. Move to own file in ApplicationTable folder once
// that's created.
export const QUERY_PARAM_NAME_TO_STATE_VAR = {
  filter: 'filterValuesByColumn',
  numRowsPerPage: 'numRowsPerPage',
  pageNum: 'pageNum',
  search: 'searchValuesByColumn',
  sort: 'sortBy',
};

export const SEARCH_OPTIONS = [
  { key: 'childEntityName', label: 'Entity Name' },
  { key: 'journeyName', label: 'Journey Name' },
  { key: 'journeyToken', label: 'Journey Token' },
  { key: 'journeyApplicationToken', label: 'Journey Application Token' },
  { key: 'journeyBatchToken', label: 'Journey Batch Token' },
  { key: 'externalGroupId', label: 'External Group ID' },
  { key: 'entityEmail', label: 'Entity Email' },
  { key: 'entityPhone', label: 'Entity Phone Number' },
  { key: 'entitySsn', label: 'Entity SSN' },
  { key: 'entityToken', label: 'Entity Token' },
  { key: 'sandboxPersonaTestToken', label: 'Sandbox Persona Test Token' },
  { key: 'shadowTestToken', label: 'Shadow Test Token' },
  { key: 'multiEnvironmentConfigToken', label: 'Multi-Environment Config Token' },
  { key: 'externalEntityId', label: 'External Entity ID' },
  { key: 'externalApplicationId', label: 'External Application ID' },
  { key: 'ingestionBatchToken', label: 'Ingestion Batch Token' },
  { key: 'ruleBasedNotificationToken', label: 'Rule-Based Notification Token' },
];

export const STATE_VAR_TO_QUERY_PARAM_NAME = (
  Object
    .entries(QUERY_PARAM_NAME_TO_STATE_VAR)
    .reduce((acc, [key, val]) => ({ ...acc, [val]: key }), {})
);

export const DISMISS_ALERT_OUTCOMES = ['dismiss', 'dismissed'];
export const PROMOTE_ALERT_OUTCOMES = ['promote to case', 'promoted to case'];

export const ALERT_REVIEW_OUTCOMES = new Set([
  ...DISMISS_ALERT_OUTCOMES,
  ...PROMOTE_ALERT_OUTCOMES,
]);

export const CREATE_NEW_INVESTIGATION_OPTION = 'create_new_investigation';

export const STATE_OPTIONS = [
  {
    label: 'Alert Review',
    value: InvestigationStateTypesMap.ALERT_REVIEW,
  },
  {
    label: 'Case Review',
    value: InvestigationStateTypesMap.CASE_REVIEW,
  },
];
